<template>
    <section class="overflow-hidden rounded-2xl border border-border bg-surface-raised" :aria-label="t('personTeams.title')" :aria-busy="isFetching">
        <h2 ref="heading" tabindex="-1" class="body-serif border-b border-border p-4 text-ink">{{ t("personTeams.title") }}</h2>
        <p class="body-small p-4 text-ink-muted empty:sr-only" role="status">
            <template v-if="isFetching">{{ t("personTeams.loading") }}</template>
        </p>
        <template v-if="!isFetching">
            <div v-if="error" class="flex flex-col items-start gap-3 p-4">
                <p role="alert" class="body-small text-ink">{{ t("personTeams.loadError") }}</p>
                <BaseButton theme="neutral" size="sm" @click="retry">{{ t("common.tryAgain") }}</BaseButton>
            </div>
            <ul v-else-if="teams.length">
                <li
                    v-for="team in teams"
                    :key="team.id"
                    ref="teamRows"
                    :data-team-id="team.id"
                    class="group grid min-h-17 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 md:grid-cols-[auto_minmax(0,1fr)_auto_auto]">
                    <BaseAvatar shape="square" size="md" class="col-start-1 row-start-1">
                        <i class="bi bi-people-fill" aria-hidden="true"></i>
                    </BaseAvatar>
                    <span class="body-serif fs-mask col-start-2 row-start-1 text-ink">{{ team.name }}</span>
                    <span
                        v-if="team.is_member"
                        class="body-small text-ink-muted opacity-0 max-md:sr-only group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100 md:col-start-3 md:row-start-1"
                        >{{ t("personTeams.onTeamToo") }}</span
                    >
                    <BaseLink
                        v-if="team.is_member && team.url"
                        :href="team.url"
                        data-team-link
                        class="team-action base-btn-neutral base-btn-sm opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100"
                        :aria-label="t('personTeams.viewNamedTeam', { name: team.name })"
                        :before="() => logUserInteraction(AmplitudeEventNames.TEAM_SELECTED, { source: props.source ?? TEAM_INTERACTION_SOURCE.PERSON_DETAIL, team_id: team.id })">
                        {{ t("personTeams.viewTeam") }}
                    </BaseLink>
                    <BaseButton
                        v-else-if="team.join_url"
                        theme="neutral"
                        size="sm"
                        class="team-action group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100"
                        :class="isJoining && selectedTeamId === team.id ? 'opacity-100' : 'opacity-0'"
                        :disabled="isJoining"
                        :aria-label="t('personTeams.joinNamedTeam', { name: team.name })"
                        @click="joinTeam(team)">
                        {{ isJoining && selectedTeamId === team.id ? t("personTeams.joining") : t("personTeams.join") }}
                    </BaseButton>
                    <p v-if="failedTeamId === team.id" role="alert" class="body-small col-span-full text-ink">{{ t("personTeams.joinError") }}</p>
                </li>
            </ul>
            <div v-else class="flex flex-col items-center justify-center px-6 py-10 text-center">
                <p class="body-serif text-ink">{{ t("personTeams.empty") }}</p>
                <p class="body-small mt-1 max-w-md text-ink-muted">{{ t("personTeams.emptyHint") }}</p>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { PersonTeam, PersonTeams } from "~vue/api/generated/team-perspective/types.gen";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ url: string; teamUrlTemplate: string; source?: TeamInteractionSource }>();
const heading = useTemplateRef<HTMLElement>("heading");
const teamRows = useTemplateRef<HTMLElement[]>("teamRows");
const { t } = useI18n();
const { data, error, isFetching, execute, abort } = useFetch(props.url, { immediate: false }).get().json<PersonTeams>();
const teams = computed(() =>
    (data.value?.teams ?? []).map((team) => ({ ...team, url: team.url ?? (team.is_member ? props.teamUrlTemplate.replace("{team_id}", team.id) : null) })),
);
const joinUrl = ref("");
const selectedTeamId = ref<string | null>(null);
const failedTeamId = ref<string | null>(null);
const {
    data: joinedTeam,
    error: joinError,
    isFetching: isJoining,
    execute: join,
    abort: abortJoin,
} = useFetch(joinUrl, teamInteractionRequest(props.source ?? TEAM_INTERACTION_SOURCE.PERSON_DETAIL), { immediate: false })
    .post()
    .json<PersonTeam>();

async function retry() {
    heading.value?.focus();
    await execute();
}

async function joinTeam(team: PersonTeam) {
    if (isJoining.value || !team.join_url) {
        return;
    }
    const joinButton = teamRows.value?.find((element) => element.dataset.teamId === team.id)?.querySelector<HTMLButtonElement>("button");
    const restoreFocus = document.activeElement === joinButton;
    logUserInteraction(AmplitudeEventNames.TEAM_JOIN_STARTED, {
        source: props.source ?? TEAM_INTERACTION_SOURCE.PERSON_DETAIL,
        entry_point: props.source ?? TEAM_INTERACTION_SOURCE.PERSON_DETAIL,
        team_id: team.id,
    });
    selectedTeamId.value = team.id;
    failedTeamId.value = null;
    joinUrl.value = team.join_url;
    await join();
    if (joinError.value || !joinedTeam.value) {
        failedTeamId.value = team.id;
        await nextTick();
        if (restoreFocus && document.activeElement === document.body) {
            joinButton?.focus();
        }
        return;
    }
    if (data.value) {
        data.value = { teams: data.value.teams.map((item) => (item.id === team.id ? joinedTeam.value! : item)) };
        await nextTick();
        const row = teamRows.value?.find((element) => element.dataset.teamId === team.id);
        if (document.activeElement === document.body || row?.contains(document.activeElement)) {
            row?.querySelector<HTMLElement>("[data-team-link]")?.focus();
        }
    }
}

onMounted(() => execute());
onBeforeUnmount(() => {
    abort();
    abortJoin();
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .team-action {
        @apply col-start-3 row-start-1 justify-self-end md:col-start-4;
    }
}
</style>
